/**
 * Textarea — wraps a native <textarea> with DS Input visual styling.
 *
 * Same field API as the other shims: label, helperText, errorText,
 * required, requiredIndicator. Plus textarea-specific: rows, maxRows, resize.
 * No Formik integration — see formik/FormikTextarea.tsx for that.
 */
import React, {forwardRef, useState} from 'react'
import styled from 'styled-components'
import {FieldHelper, FieldLabel, FieldWrapper, RequiredIndicator} from './FieldParts'

export interface TextareaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> {
  /** Label text above the textarea */
  label?: string
  /** Hint text below the field (hidden when errorText is shown) */
  helperText?: string
  /** Error text below the field, right-aligned. Shows red border. */
  errorText?: string
  /** Show a required indicator (*) next to the label */
  required?: boolean
  /** Custom required indicator (defaults to "*") */
  requiredIndicator?: string
  /** Number of visible text rows */
  rows?: number
  /** CSS resize behavior */
  resize?: 'none' | 'vertical' | 'horizontal' | 'both'
  /** DS size variant */
  size?: 'sm' | 'md' | 'lg'
  /** Whether the field takes full width (default: true) */
  fullWidth?: boolean
}

const sizeStyles = {
  sm: {fontSize: '13px', padding: 'var(--space-2) var(--space-3)'},
  md: {fontSize: '14px', padding: '10px var(--space-3)'},
  lg: {fontSize: '15px', padding: 'var(--space-3) var(--space-3)'},
}

const StyledTextarea = styled.textarea<{
  $error: boolean
  $size: 'sm' | 'md' | 'lg'
  $resize: string
}>`
  font-family: var(--font-sans);
  font-size: ${({$size}) => sizeStyles[$size].fontSize};
  padding: ${({$size}) => sizeStyles[$size].padding};
  color: var(--neutral-900);
  letter-spacing: var(--ls-normal);
  background: var(--white);
  border: 1px solid ${({$error}) => ($error ? 'var(--error-500)' : 'var(--neutral-300)')};
  border-radius: var(--radius-sm);
  resize: ${({$resize}) => $resize};
  width: 100%;
  min-width: 0;
  outline: none;
  transition: border-color var(--motion-fast) var(--motion-ease),
    box-shadow var(--motion-fast) var(--motion-ease);

  &:hover {
    border-color: ${({$error}) => ($error ? 'var(--error-500)' : 'var(--neutral-400)')};
  }

  &:focus {
    border-color: var(--focus-border);
    box-shadow: var(--focus-ring);
  }

  &::placeholder {
    color: var(--neutral-500);
  }

  &:disabled {
    cursor: not-allowed;
    color: var(--neutral-500);
  }
`

let textareaCounter = 0

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      helperText,
      errorText,
      required,
      requiredIndicator = '*',
      rows = 3,
      resize = 'vertical',
      size = 'md',
      fullWidth = true,
      id,
      ...rest
    },
    ref,
  ) => {
    const [resolvedId] = useState(() => id ?? `textarea-${++textareaCounter}`)
    const hasError = Boolean(errorText)

    return (
      <FieldWrapper $fullWidth={fullWidth}>
        {label && (
          <FieldLabel htmlFor={resolvedId}>
            {label}
            {required && (
              <RequiredIndicator aria-hidden="true"> {requiredIndicator}</RequiredIndicator>
            )}
          </FieldLabel>
        )}
        <StyledTextarea
          ref={ref}
          id={resolvedId}
          rows={rows}
          $error={hasError}
          $size={size}
          $resize={resize}
          aria-invalid={hasError || undefined}
          aria-describedby={errorText ? `${resolvedId}-error` : undefined}
          required={required}
          {...rest}
        />
        {errorText && (
          <FieldHelper id={`${resolvedId}-error`} $error role="alert">
            {errorText}
          </FieldHelper>
        )}
        {!errorText && helperText && <FieldHelper>{helperText}</FieldHelper>}
      </FieldWrapper>
    )
  },
)
Textarea.displayName = 'Textarea'

export default Textarea
